import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50 p-6" }
const _hoisted_2 = { class: "mx-auto space-y-6" }
const _hoisted_3 = { class: "flex items-center justify-between" }
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = { class: "relative" }
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4" }
const _hoisted_7 = { class: "flex items-center justify-between" }
const _hoisted_8 = { class: "text-3xl font-bold text-gray-900 mt-2" }
const _hoisted_9 = { class: "w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" }
const _hoisted_10 = { class: "flex items-center justify-between" }
const _hoisted_11 = { class: "w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" }
const _hoisted_12 = { class: "flex items-center justify-between" }
const _hoisted_13 = { class: "text-3xl font-bold text-purple-600 mt-2" }
const _hoisted_14 = { class: "w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" }
const _hoisted_15 = { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" }
const _hoisted_16 = { class: "flex items-start justify-between mb-4" }

import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTransactionStore } from '@/store/use-transaction-store';
import { useRouter } from 'vue-router';
import { Button } from '@/components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  FileText,
  Search,
  ArrowRight,
  BarChart3,
  FileSpreadsheet,
  Calendar,
  RefreshCw,
  Sunset,
  Merge,
  UserCheck,
} from 'lucide-vue-next';

interface Report {
  name: string;
  title: string;
  route: string;
  description: string;
  buttonText: string;
  reportType?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ReportTypesView',
  setup(__props) {

const router = useRouter();

// Get store
const transactionStore = useTransactionStore()

const reports = ref<Report[]>([
  {
    name: 'Rollover Report',
    title: 'Rollover Report',
    route: '/transactions',
    reportType: 'rollover',
    description: 'Rollover Report provides operational transparency into treatment plans extended into new benefit periods. It highlights plans that remain active, suspended, or expired, including carried-over balances, remaining allowed quantities, and updated eligibility.',
    buttonText: 'Rollover Report'
  },
  {
    name: 'Sync Practice Report',
    title: 'Sync Practices',
    route: '/transactions',
    reportType: 'syncing',
    description: 'The Sync Practice Types Report provides visibility into how practice type configurations are synchronized across systems. It highlights mismatches, missing mappings, and successful alignments between source and downstream platforms.',
    buttonText: 'Sync Practices Report'
  },
  {
    name: 'Tariff Codes Upload Report',
    title: 'Tariff Codes Upload Report',
    route: '/transactions',
    reportType: 'add_tariff_codes',
    description: 'This report facilitates the bulk upload of new or updated tariff codes to a Treatment Plan.',
    buttonText: 'Tariff Codes Upload Report'
  },
  {
    name: 'Tariff Codes Merge Report',
    title: 'Tariff Codes Merge Report',
    route: '/transactions',
    reportType: 'merge_tariff_codes',
    description: 'The Tariff Codes Merge Report provides visibility into the merging process of duplicate or similar tariff codes into a single unified code',
    buttonText: 'Tariff Codes Merge Report'
  },
  {
    name: 'Tariff Code Allowed Quantity Report',
    title: 'Tariff Code Allowed Quantity Report',
    route: '/transactions',
    reportType: 'update_allowed_qty',
    description: 'The Tariff Code Allowed Quantity Report provides visibility into the update of allowed quantities process for each tariff code, including their current usage, remaining balances, and impact across treatment plans, billing, and operational systems.',
    buttonText: 'Tariff Code Allowed Quantity Report'
  },
  {
    name: 'Tariff Code Sunsetting Report',
    title: 'Tariff Code Sunsetting Report',
    route: '/transactions',
    reportType: 'sunset',
    description: 'The Tariff Codes Sunsetting Report provides visibility into tariff codes scheduled for retirement, including their current usage, dependencies, and impact across treatment plans, billing, and operational systems.',
    buttonText: 'Tariff Code Sunsetting Report'
  },
  {
    name: 'Membership Status Report',
    title: 'Membership Status Report',
    route: '/transactions',
    reportType: 'membership_status_check',
    description: 'The Membership Status Report provides comprehensive insights into the status of individual memberships, including their active, suspended, or expired status, and impact on treatment plans, billing, and operational systems.',
    buttonText: 'Membership Status Report'
  }
]);

const searchQuery = ref('');

const filteredReports = ref(reports.value.filter(report =>
  !searchQuery.value ||
  report.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
  report.description.toLowerCase().includes(searchQuery.value.toLowerCase())
));

const getReportIcon = (reportName: string) => {
  switch (reportName) {
    case 'Rollover Report':
      return Calendar;
    case 'Sync Practice Report':
      return RefreshCw;
    case 'Tariff Code Sunsetting Report':
      return Sunset;
    case 'Tariff Codes Upload Report':
      return FileSpreadsheet;
     case 'Tariff Codes Merge Report':
      return Merge;
    case 'Tariff Code Allowed Quantity Report':
      return BarChart3;
    case 'Membership Status Report':
      return UserCheck; 
    default:
      return FileText;
  }
};

const getReportColor = (reportName: string) => {
  switch (reportName) {
    case 'Rollover Report':
      return 'bg-blue-100 text-blue-600';
    case 'Sync Practice Report':
      return 'bg-green-100 text-green-600';
    case 'Tariff Code Sunsetting Report':
      return 'bg-orange-100 text-orange-600';
    case 'Tariff Codes Merge Report':
      return 'bg-purple-100 text-purple-600';
    case 'Membership Status Report':
      return 'bg-yellow-100 text-yellow-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const setTranasctionTypeNavigateToReport = (route: string, transactionType: string) => {
  // Here we goig to set Transaction type
  transactionStore.setTransactionType(transactionType)
  router.push(route);
};

const handleSearch = () => {
  filteredReports.value = reports.value.filter(report =>
    !searchQuery.value ||
    report.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    report.description.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("div", null, [
          _createElementVNode("h1", { class: "text-3xl font-bold text-gray-900" }, "Reports Dashboard"),
          _createElementVNode("p", { class: "text-gray-600 mt-1" }, "Access and generate operational reports")
        ], -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(Badge), {
            variant: "outline",
            class: "px-3 py-1"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(filteredReports.value.length) + " Reports Available ", 1)
            ]),
            _: 1
          })
        ])
      ]),
      _createVNode(_unref(Card), null, {
        default: _withCtx(() => [
          _createVNode(_unref(CardContent), { class: "pt-6" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(Search), { class: "absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" }),
                _createVNode(_unref(Input), {
                  modelValue: searchQuery.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                  onInput: handleSearch,
                  placeholder: "Search reports by name or description...",
                  class: "pl-10 h-12 text-base"
                }, null, 8, ["modelValue"])
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(Card), null, {
          default: _withCtx(() => [
            _createVNode(_unref(CardContent), { class: "pt-6" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", null, [
                    _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600" }, "Total Reports", -1)),
                    _createElementVNode("p", _hoisted_8, _toDisplayString(reports.value.length), 1)
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    _createVNode(_unref(FileSpreadsheet), { class: "h-6 w-6 text-blue-600" })
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(Card), null, {
          default: _withCtx(() => [
            _createVNode(_unref(CardContent), { class: "pt-6" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_10, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", null, [
                    _createElementVNode("p", { class: "text-sm font-medium text-gray-600" }, "Operational Reports"),
                    _createElementVNode("p", { class: "text-3xl font-bold text-green-600 mt-2" }, "3")
                  ], -1)),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(_unref(BarChart3), { class: "h-6 w-6 text-green-600" })
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(Card), null, {
          default: _withCtx(() => [
            _createVNode(_unref(CardContent), { class: "pt-6" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", null, [
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600" }, "Available Now", -1)),
                    _createElementVNode("p", _hoisted_13, _toDisplayString(filteredReports.value.length), 1)
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(_unref(FileText), { class: "h-6 w-6 text-purple-600" })
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_15, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredReports.value, (report) => {
          return (_openBlock(), _createBlock(_unref(Card), {
            key: report.name,
            class: "hover:shadow-lg transition-shadow cursor-pointer group",
            onClick: ($event: any) => (setTranasctionTypeNavigateToReport(report.route, report.reportType))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(CardHeader), null, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", {
                      class: _normalizeClass(['w-14 h-14 rounded-lg flex items-center justify-center', getReportColor(report.name)])
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(getReportIcon(report.name)), { class: "h-7 w-7" }))
                    ], 2),
                    _createVNode(_unref(Badge), {
                      variant: "secondary",
                      class: "text-xs bg-gray-100 rounded-lg hover:bg-green-500 hover:text-white"
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode(" Report ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(_unref(CardTitle), { class: "text-xl group-hover:text-blue-600 transition-colors" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(report.title), 1)
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(_unref(CardDescription), { class: "mt-2 text-sm leading-relaxed min-h-[60px]" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(report.description), 1)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1024),
              _createVNode(_unref(CardContent), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    variant: "outline",
                    class: "w-full text-white bg-gradient-to-r from-sky-800 to-[#be9b2d] group-hover:text-white group-hover:border-sky-700 transition-all",
                    onClick: _withModifiers(($event: any) => (setTranasctionTypeNavigateToReport(report.route, report.reportType)), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(report.buttonText) + " ", 1),
                      _createVNode(_unref(ArrowRight), { class: "ml-2 h-4 w-4" })
                    ]),
                    _: 2
                  }, 1032, ["onClick"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1032, ["onClick"]))
        }), 128))
      ]),
      (filteredReports.value.length === 0)
        ? (_openBlock(), _createBlock(_unref(Card), {
            key: 0,
            class: "border-dashed"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(CardContent), { class: "flex flex-col items-center justify-center py-12" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FileText), { class: "h-16 w-16 text-gray-400 mb-4" }),
                  _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-gray-600 text-lg font-medium" }, "No reports found", -1)),
                  _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-gray-500 text-sm mt-1" }, "Try adjusting your search query", -1)),
                  _createVNode(_unref(Button), {
                    variant: "outline",
                    class: "mt-4",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => {searchQuery.value = ''; handleSearch()})
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode(" Clear Search ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})